import { useCallback, useEffect, useRef, useState } from 'react';
import { PictureOutlined } from '@ant-design/icons';
import { Badge, Button, Empty, Image, Input, Spin, Upload, message } from 'antd';
import { io } from 'socket.io-client';
import { fileUrl, post } from '../../api';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const PAGE_SIZE = 30;

function formatTime(value) {
  if (!value) {
    return '';
  }
  const text = String(value).replace('T', ' ');
  const today = new Date();
  const date = text.slice(0, 10);
  const todayText = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`;
  return date === todayText ? text.slice(11, 16) : text.slice(5, 16);
}

async function uploadImage(file) {
  const token = localStorage.getItem('token');
  const headers = {};
  if (token) {
    headers.token = token;
  }
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/file/upload`, { method: 'POST', headers, body: form });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * 平台后台：商家与平台的聊天。左侧店铺会话，右侧消息面板，Socket 实时收发。
 */
export default function Chats() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState('');
  const [messages, setMessages] = useState([]);
  const [msgTotal, setMsgTotal] = useState(0);
  const [msgPage, setMsgPage] = useState(1);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const selectedRef = useRef('');
  const listRef = useRef(null);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const scrollToBottom = () => {
    const node = listRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  };

  const loadList = useCallback(async (current = 1) => {
    setLoadingList(true);
    try {
      const rsp = await post('/admin/chat/list', { page: current, size: 50 });
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setList(rsp.data.list || []);
      setTotal(rsp.data.total || 0);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId, targetPage = 1, append = false) => {
    if (!conversationId) {
      return;
    }
    setLoadingMsg(true);
    try {
      const rsp = await post('/admin/chat/messages', {
        id: conversationId,
        page: targetPage,
        size: PAGE_SIZE,
      });
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setMessages((current) => (append ? [...(rsp.data.list || []), ...current] : rsp.data.list || []));
      setMsgTotal(rsp.data.total || 0);
      setMsgPage(targetPage);
      if (!append) {
        setTimeout(scrollToBottom, 0);
      }
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setLoadingMsg(false);
    }
  }, []);

  useEffect(() => {
    loadList(1);
  }, [loadList]);

  // 建立 Socket 连接，实时收消息
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      return undefined;
    }
    const client = io(API_BASE || window.location.origin, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      query: { token, role: 'admin' },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socketRef.current = client;
    client.on('connect', () => setConnected(true));
    client.on('disconnect', () => setConnected(false));
    client.on('chat:message', (data) => {
      loadList(1);
      if (!data || data.conversationId !== selectedRef.current) {
        return;
      }
      setMessages((current) => {
        if (current.some((item) => item.id === data.id)) {
          return current;
        }
        return [...current, data];
      });
      setTimeout(scrollToBottom, 0);
      client.emit('chat:read', { id: data.conversationId });
    });
    client.on('chat:error', (msg) => message.error(msg || '消息发送失败'));
    return () => {
      client.disconnect();
      socketRef.current = null;
    };
  }, [loadList]);

  const openConversation = (item) => {
    setSelected(item.id);
    setMessages([]);
    setMsgTotal(0);
    setMsgPage(1);
    setText('');
    loadMessages(item.id, 1);
    setList((current) => current.map((row) => (row.id === item.id ? { ...row, unread: 0 } : row)));
  };

  const sendPayload = async (payload) => {
    const client = socketRef.current;
    if (client && client.connected) {
      client.emit('chat:send', { id: selected, ...payload });
      return true;
    }
    const rsp = await post('/admin/chat/send', { id: selected, ...payload });
    if (rsp.code) {
      message.error(rsp.msg || '发送失败');
      return false;
    }
    setMessages((current) => [...current, rsp.data]);
    setTimeout(scrollToBottom, 0);
    return true;
  };

  const send = async () => {
    const content = text.trim();
    if (!content || sending || !selected) {
      return;
    }
    setSending(true);
    setText('');
    try {
      const ok = await sendPayload({ content });
      if (!ok) {
        setText(content);
      }
    } catch (e) {
      message.error('网络异常，请稍后再试');
      setText(content);
    } finally {
      setSending(false);
    }
  };

  const sendImage = async (file) => {
    if (uploading || !selected) {
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadImage(file);
      if (uploaded.code) {
        message.error(uploaded.msg || '上传失败');
        return;
      }
      await sendPayload({ type: 1, fileId: uploaded.data.id });
    } catch (e) {
      message.error('图片上传失败，请稍后再试');
    } finally {
      setUploading(false);
    }
  };

  const current = list.find((item) => item.id === selected);

  return (
    <div className="chat-page">
      <aside className="chat-page-side">
        <div className="chat-page-side-head">
          <span>商家消息</span>
          <Badge count={total} showZero size="small" color="#1677ff" />
        </div>
        <div className="chat-page-convs">
          {loadingList && list.length === 0 ? (
            <div className="chat-page-empty">
              <Spin />
            </div>
          ) : list.length === 0 ? (
            <Empty description="暂无商家消息" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            list.map((item) => (
              <button
                type="button"
                key={item.id}
                className={item.id === selected ? 'chat-page-conv active' : 'chat-page-conv'}
                onClick={() => openConversation(item)}
              >
                <div className="chat-page-conv-top">
                  <strong>{item.storeName || item.storeId}</strong>
                  <span>{formatTime(item.lastTime)}</span>
                </div>
                <div className="chat-page-conv-bottom">
                  <span>{item.lastContent || '暂无消息'}</span>
                  {item.unread ? <Badge count={item.unread} size="small" /> : null}
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      <section className="chat-page-room">
        {!selected ? (
          <div className="chat-page-empty">
            <Empty description="选择左侧店铺开始聊天" />
          </div>
        ) : (
          <>
            <div className="chat-page-room-head">
              <strong>{current?.storeName || '店铺'}</strong>
              <span className={connected ? 'chat-page-status online' : 'chat-page-status'}>
                {connected ? '实时在线' : '连接中断，使用接口发送'}
              </span>
            </div>

            <div className="chat-page-messages" ref={listRef}>
              {msgPage * PAGE_SIZE < msgTotal ? (
                <div className="chat-page-more">
                  <Button size="small" loading={loadingMsg} onClick={() => loadMessages(selected, msgPage + 1, true)}>
                    查看更早的消息
                  </Button>
                </div>
              ) : null}
              {loadingMsg && messages.length === 0 ? (
                <div className="chat-page-empty">
                  <Spin />
                </div>
              ) : null}
              {messages.map((item) => (
                <div key={item.id} className={item.senderType === 'admin' ? 'chat-page-bubble mine' : 'chat-page-bubble'}>
                  {item.type === 1 ? (
                    <Image
                      src={fileUrl(item.fileId, 480)}
                      preview={{ src: fileUrl(item.fileId, 1080) }}
                      width={160}
                    />
                  ) : (
                    <div className="chat-page-bubble-text">{item.content}</div>
                  )}
                  <span className="chat-page-bubble-time">{formatTime(item.createTime)}</span>
                </div>
              ))}
            </div>

            <div className="chat-page-composer">
              <Upload
                accept="image/*"
                showUploadList={false}
                customRequest={({ file }) => sendImage(file)}
                disabled={uploading}
              >
                <Button icon={<PictureOutlined />} loading={uploading} />
              </Upload>
              <Input.TextArea
                value={text}
                autoSize={{ minRows: 1, maxRows: 4 }}
                maxLength={2000}
                placeholder="输入消息，Enter 发送，Shift+Enter 换行"
                onChange={(e) => setText(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <Button type="primary" loading={sending} disabled={!text.trim()} onClick={send}>
                发送
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
